import { useQuery } from '@tanstack/react-query';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { MuiTelInputCountry } from 'mui-tel-input';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Grid } from 'src/components/Grid';
import { Meta } from 'src/components/Meta';
import { StickyCard } from 'src/components/StickyCard';
import { useSession } from 'src/contexts/SessionContext';
import { DebouncedFormik } from 'src/shared/Forms/DebouncedFormik';
import { FormPhoneInput } from 'src/shared/Forms/Fields/FormPhoneInput';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import * as Yup from 'yup';

import {
  CreateFranchiseFeePayerParams,
  FranchiseFeePayer,
  UpdateFranchiseFeePayerParams,
  assignedPlatformIdsQueryKey,
  fetchAssignedPlatformIds,
} from '../../../models/hq/franchise-fee-payer/franchise-fee-payer';
import SiteSelect from '../../../shared/Forms/Selects/SiteSelect';

const gridItemCommonSize = {
  sm: 6,
  xs: 12,
};

interface FranchiseFeePayerFormProps {
  payer?: FranchiseFeePayer;
  onSave: (
    values: CreateFranchiseFeePayerParams | UpdateFranchiseFeePayerParams
  ) => void;
  onCancel: () => void;
  title: string;
  isNew?: boolean;
}

const FranchiseFeePayerForm: React.FC<FranchiseFeePayerFormProps> = ({
  payer,
  onSave,
  onCancel,
  title,
  isNew = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFeePayers',
  });
  const { session } = useSession();

  // Fetch all assigned platform IDs
  const { data: assignedPlatformIds = [] } = useQuery({
    queryKey: assignedPlatformIdsQueryKey(),
    queryFn: fetchAssignedPlatformIds,
  });

  // Compute excluded platform IDs (all assigned platforms minus current payer's platforms)
  const excludedPlatformIds = useMemo(() => {
    // For editing, exclude current payer's platforms from the exclusion list
    // so they can keep their own platforms
    const currentPayerPlatformIds = payer?.platformIds || [];

    // Safely merge assigned platform IDs, only adding payer.platformId if it exists and is not already included
    const platformIds = Array.isArray(assignedPlatformIds)
      ? [...assignedPlatformIds]
      : [];
    if (payer?.platformId !== undefined && payer?.platformId !== null) {
      platformIds.push(Number(payer.platformId));
    }
    return platformIds.filter(
      (platformId) => !currentPayerPlatformIds.includes(platformId)
    );
  }, [assignedPlatformIds, payer?.platformIds, payer?.platformId]);

  // Unified form configuration
  const formikConfig = {
    initialValues: {
      firstName: payer?.firstName || '',
      lastName: payer?.lastName || '',
      email: payer?.email || '',
      phoneNumber: payer?.phoneNumber || '',
      notes: payer?.notes || '',
      sites: {
        selected: payer?.platformIds?.map(String) || [],
      },
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(t('First name is required')).max(255),
      lastName: Yup.string().required(t('Last name is required')).max(255),
      email: Yup.string()
        .email(t('Invalid email'))
        .required(t('Email is required')),
      phoneNumber: Yup.string()
        .test('phoneNumber', t('Invalid phone number'), (value) => {
          if (!value) return true;
          return isPossiblePhoneNumber(value || '');
        })
        .nullable(),
      notes: Yup.string().max(1000),
    }),
    onSubmit: async (values) => {
      const { sites, ...basicValues } = values;
      onSave({
        ...basicValues,
        platformIds: sites.selected.map(Number),
      });
    },
  };

  return (
    <>
      <Meta title={title} brand={session?.brand} />
      <DebouncedFormik enableReinitialize={true} {...formikConfig}>
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <>
            {/* Basic Information */}
            <StickyCard title={t('basicInformation')} sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                <Grid size={{ ...gridItemCommonSize }}>
                  <FormTextField
                    variant="outlined"
                    name="firstName"
                    label={t('firstName')}
                    required
                  />
                </Grid>

                <Grid size={{ ...gridItemCommonSize }}>
                  <FormTextField
                    variant="outlined"
                    name="lastName"
                    label={t('lastName')}
                    required
                  />
                </Grid>

                <Grid size={{ ...gridItemCommonSize }}>
                  <FormTextField
                    name="email"
                    type="email"
                    label={t('email')}
                    required
                  />
                </Grid>

                <Grid size={{ ...gridItemCommonSize }}>
                  <FormPhoneInput
                    name="phoneNumber"
                    label={t('phoneNumber')}
                    defaultCountry={session?.country as MuiTelInputCountry}
                    preferredCountries={
                      [session?.country || 'US', 'US'] as MuiTelInputCountry[]
                    }
                    variant="outlined"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <SiteSelect
                    label={t('assignedPlatforms')}
                    values={values.sites.selected}
                    onChange={(selectedIds) => {
                      setFieldValue('sites.selected', selectedIds);
                    }}
                    platformsOnly={true}
                    excludeSites={excludedPlatformIds}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormTextField
                    name="notes"
                    label={t('notes')}
                    multiline
                    rows={3}
                    placeholder={t('Add any notes about this payer...')}
                  />
                </Grid>
              </Grid>
            </StickyCard>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                mt: 4,
              }}
            >
              <Button variant="bordered" onClick={onCancel}>
                {t('cancel')}
              </Button>
              <Button
                variant="prominent"
                onClick={submitForm}
                disabled={isSubmitting}
              >
                {isNew ? t('createPayer') : t('save')}
              </Button>
            </Box>
          </>
        )}
      </DebouncedFormik>
    </>
  );
};

export default FranchiseFeePayerForm;
